import { EventEmitter } from 'events';
import { URI, WorkspaceFolder } from 'vscode-languageserver-protocol';
import { Disposable } from '@gitlab-org/disposable';
import { createInterfaceId, Injectable } from '@gitlab-org/di';
import { TextDocument } from 'vscode-languageserver-textdocument';
import { DocumentService } from '../document_service';
import { ConfigService } from '../config_service';
import { TextDocumentChangeListenerType } from '../text_document_change_listener_type';
import { DuoProjectAccessCache, DuoProjectAccessChecker } from '../services/duo_access';
import { DuoProjectStatus } from '../services/duo_access/project_access_checker';
import { StateCheck, StateCheckChangedEventData } from './state_check';
import { DUO_DISABLED_FOR_PROJECT } from './feature_state_management_types';

export interface ProjectDuoAccessCheck extends StateCheck<typeof DUO_DISABLED_FOR_PROJECT> {}

export const ProjectDuoAccessCheck =
  createInterfaceId<ProjectDuoAccessCheck>('ProjectDuoAccessCheck');

@Injectable(ProjectDuoAccessCheck, [
  DocumentService,
  ConfigService,
  DuoProjectAccessChecker,
  DuoProjectAccessCache,
])
export class DefaultProjectDuoAccessCheck implements StateCheck<typeof DUO_DISABLED_FOR_PROJECT> {
  #subscriptions: Disposable[] = [];

  #stateEmitter = new EventEmitter();

  #hasDuoAccess?: boolean;

  #duoProjectAccessChecker: DuoProjectAccessChecker;

  #configService: ConfigService;

  #currentDocument?: TextDocument;

  #isEnabledInSettings = true;

  constructor(
    documentService: DocumentService,
    configService: ConfigService,
    duoProjectAccessChecker: DuoProjectAccessChecker,
    duoProjectAccessCache: DuoProjectAccessCache,
  ) {
    this.#configService = configService;
    this.#duoProjectAccessChecker = duoProjectAccessChecker;

    this.#subscriptions.push(
      documentService.onDocumentChange(async (event, handlerType) => {
        if (handlerType === TextDocumentChangeListenerType.onDidSetActive) {
          this.#currentDocument = event.document;

          await this.#checkIfProjectHasDuoAccess();
        }
      }),

      configService.onConfigChange(async (config) => {
        this.#isEnabledInSettings = Boolean(config.client.duo?.enabledWithoutGitlabProject);

        await this.#checkIfProjectHasDuoAccess();
      }),

      duoProjectAccessCache.onDuoProjectCacheUpdate(() => this.#checkIfProjectHasDuoAccess()),
    );
  }

  async init() {
    await this.#checkIfProjectHasDuoAccess();
  }

  onChanged(listener: (data: StateCheckChangedEventData) => void): Disposable {
    this.#stateEmitter.on('change', listener);
    return {
      dispose: () => this.#stateEmitter.removeListener('change', listener),
    };
  }

  get engaged() {
    return !this.#hasDuoAccess;
  }

  async #checkIfProjectHasDuoAccess(): Promise<void> {
    this.#hasDuoAccess = this.#isEnabledInSettings;

    if (!this.#currentDocument) {
      this.#stateEmitter.emit('change', this);
      return;
    }

    const workspaceFolder = await this.#getWorkspaceFolderForUri(this.#currentDocument.uri);

    if (workspaceFolder) {
      const result = this.#duoProjectAccessChecker.checkProjectStatus(
        this.#currentDocument.uri,
        workspaceFolder,
      );

      if (result?.status === DuoProjectStatus.DuoEnabled) {
        this.#hasDuoAccess = true;
      } else if (result?.status === DuoProjectStatus.DuoDisabled) {
        this.#hasDuoAccess = false;
      }
    }

    this.#stateEmitter.emit('change', this);
  }

  id = DUO_DISABLED_FOR_PROJECT;

  details = 'Duo features are disabled for this project';

  async #getWorkspaceFolderForUri(uri: URI): Promise<WorkspaceFolder | undefined> {
    const workspaceFolders = await this.#configService.get('client.workspaceFolders');

    return workspaceFolders?.find((folder) => uri.startsWith(folder.uri));
  }

  dispose() {
    this.#subscriptions.forEach((s) => s.dispose());
  }
}
